import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Users } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { useDjLook } from "../lib/djLook";
import { startPhoneSender, whyText, MAX_BYTES } from "../lib/djXfer";
import { lit, Deck, Library, LookPicker, newer, sleep, say } from "./DjBooth";
import "../components/djbooth.css";

// A friend's page for the DJ Booth party (route /dj/join/:invite). The host (a Premium member) turns on "Invite friends" on the DJ Booth
// page and sends this link. A person with no login sees who is playing and a button to create a free login (after signing up or in, the
// site brings them straight back here: Auth.js reads `dtm_dj_next`). A person with a login joins, takes ONE free deck, and then loads
// songs, plays, pauses, jumps ahead and sets the volume and tempo on that deck only. The crossfader, the Fade and Master stay with the host.
// Everything goes through the server (backend/dj.py, the /dj/party/... routes): this page sends commands and asks "has anything changed?".
// While the party runs the host's Premium covers the songs (a friend plays Premium songs in full); the 18+ check is always the friend's own.
// A friend can also play files from their OWN phone ("From my phone" in the song list). The page keeps the chosen files in memory only. When a
// friend loads one on their deck, the TV page asks this page for it and the file goes straight to the TV (lib/djXfer.js: a WebRTC data
// channel, through Cloudflare's relay when the two cannot reach each other). Nothing is uploaded or saved. The deck says Ready when the TV
// has all of it. This page must stay open until then, so it asks the phone to keep the screen on while a file is being sent.
const MAX_FILES = 30;
const isVideoFile = (f) => /^video\//.test(f.type || "") || /\.(mp4|m4v|mov|webm)$/i.test(f.name || "");
const isMusicFile = (f) => /^audio\//.test(f.type || "") || /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac)$/i.test(f.name || "");
const titleOf = (name) => String(name || "Video").replace(/\.[^.]+$/, "").replace(/[_]+/g, " ").replace(/\s+/g, " ").trim() || "Video";
const pageId = Math.random().toString(36).slice(2, 6);

function rememberParty(invite) {
  try { sessionStorage.setItem("dtm_dj_next", `/dj/join/${invite}`); } catch { /* no storage: the person opens the link again */ }
}

export default function DjJoin() {
  const { invite } = useParams();
  const { user, ready } = useAuth();
  const { setPlaying } = usePlayer();
  const look = useDjLook();
  const [phase, setPhase] = useState("loading");     // loading | guest | in | over | stuck | left
  const [info, setInfo] = useState(null);            // what a person without a login is shown
  const [booth, setBooth] = useState(null);          // what a friend is shown
  const [msg, setMsg] = useState("");
  const [kick, setKick] = useState(0);
  const [busy, setBusy] = useState(false);
  const leaving = useRef(false);                  // set the moment the friend presses Leave, so the listening loop does not join them again
  const [myFiles, setMyFiles] = useState([]);        // the files this friend chose on their phone: [{ k, t, a, size, mime, sig }]
  const [sendInfo, setSendInfo] = useState(null);     // what the sending is doing: { vid, phase, pct, why, relay }
  const fileObjs = useRef(new Map());                 // key -> the File itself (kept in memory only)
  const fileCount = useRef(0);
  const pickRef = useRef(null);
  const [rights, setRightsState] = useState(() => { try { return localStorage.getItem("dtm_dj_rights") === "1"; } catch { return false; } });
  const setRights = (v) => {
    setRightsState(!!v);
    try { if (v) localStorage.setItem("dtm_dj_rights", "1"); else localStorage.removeItem("dtm_dj_rights"); } catch { /* ignore */ }
  };
  const uid = user ? user.id : null;
  const premium = !!user && (user.is_premium || user.role === "admin");

  useEffect(() => { setPlaying(false); }, [setPlaying]);        // the site's own player must not play under this page

  useEffect(() => {
    const title = document.title;
    document.title = "DJ Party | Dan The Man TV";
    const robots = document.createElement("meta");              // the link holds a private invite: keep it out of search engines
    robots.setAttribute("name", "robots");
    robots.setAttribute("content", "noindex, nofollow");
    document.head.appendChild(robots);
    return () => { document.title = title; robots.remove(); };
  }, []);

  // A person with no login: who is playing, refreshed every few seconds.
  useEffect(() => {
    if (!ready || uid) return undefined;
    let dead = false;
    let timer = null;
    const load = async () => {
      try {
        const r = await api.get("/dj/party/info", { params: { invite } });
        if (dead) return;
        setInfo(r.data);
        setPhase("guest");
      } catch (e) {
        if (dead) return;
        if (e?.response?.status === 404) { setPhase("over"); return; }
      }
      timer = setTimeout(load, 6000);
    };
    load();
    return () => { dead = true; clearTimeout(timer); };
  }, [ready, uid, invite]);

  // A person with a login: join, then listen for changes (the answer comes back the moment something changes, or after about 15 seconds).
  useEffect(() => {
    if (!ready || !uid) return undefined;
    let dead = false;
    (async () => {
      let since = -1;
      let joined = false;
      while (!dead && !leaving.current) {
        try {
          if (!joined) {
            const r = await api.post("/dj/party/join", { invite });
            if (dead) return;
            joined = true;
            since = -1;
            setBooth((prev) => newer(prev, r.data.booth));
            setPhase("in");
            setMsg("");
          }
          const r = await api.get("/dj/party/wait", { params: { invite, since }, timeout: 30000 });
          if (dead) return;
          const b = r.data.booth;
          since = b.v;
          setBooth((prev) => newer(prev, b));
        } catch (e) {
          if (dead || leaving.current) return;
          const st = e?.response?.status;
          const d = e?.response?.data?.detail;
          if (st === 403 && d === "not_joined") { joined = false; continue; }
          if (st === 404) { setPhase("over"); setBooth(null); return; }
          if (st === 409 || st === 403) { setPhase("stuck"); setMsg(typeof d === "string" ? d : "You cannot join this party."); return; }
          if (st === 401) return;
          await sleep(2000);
        }
      }
    })();
    return () => { dead = true; };
  }, [ready, uid, invite, kick]);

  // One answer from the server about something the friend did.
  const act = useCallback(async (path, body) => {
    try {
      const r = await api.post(path, { invite, ...body });
      if (r.data && r.data.booth) setBooth((prev) => newer(prev, r.data.booth));
      return true;
    } catch (e) {
      const st = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (st === 404) { setPhase("over"); return false; }
      if (st === 403 && d === "not_joined") { setKick((k) => k + 1); return false; }
      say(e, "That did not go through.");
      return false;
    }
  }, [invite]);
  const send = useCallback((body) => act("/dj/party/cmd", body), [act]);
  const take = async (deck) => { setBusy(true); await act("/dj/party/take", { deck }); setBusy(false); };
  const give = async () => { setBusy(true); await act("/dj/party/give", {}); setBusy(false); };
  const leave = async () => {
    setBusy(true);
    leaving.current = true;
    await act("/dj/party/leave", {});
    setBusy(false);
    setBooth(null);
    setPhase("left");
  };
  const rejoin = () => { leaving.current = false; setPhase("loading"); setKick((k) => k + 1); };
  const fetchFiles = useCallback(() => api.get("/dj/party/files", { params: { invite } }), [invite]);

  // ---- files from this phone ----
  const addMine = (list) => {
    const sigs = new Set(myFiles.map((f) => f.sig));
    const next = [...myFiles];
    let notMedia = 0;
    let tooBig = 0;
    Array.from(list || []).forEach((f) => {
      const music = !isVideoFile(f) && isMusicFile(f);
      if ((!music && !isVideoFile(f)) || !f.size) { notMedia += 1; return; }
      if (f.size > MAX_BYTES) { tooBig += 1; return; }
      const sig = `${f.name}|${f.size}|${f.lastModified}`;
      if (sigs.has(sig) || next.length >= MAX_FILES) return;
      sigs.add(sig);
      fileCount.current += 1;
      const k = `${pageId}${fileCount.current}`;
      fileObjs.current.set(k, f);
      next.push({ k, t: titleOf(f.name), a: music, size: f.size, mime: f.type || "", sig });
    });
    if (notMedia) toast.error("Pick MP4 videos, or MP3 or WAV music.");
    if (tooBig) toast.error(`A file is bigger than ${Math.round(MAX_BYTES / (1024 * 1024))} MB. Pick a smaller or shorter one.`);
    if (next.length !== myFiles.length) setMyFiles(next);
  };
  const removeMine = (k) => { fileObjs.current.delete(k); setMyFiles((prev) => prev.filter((f) => f.k !== k)); };

  const me = booth && booth.party && booth.party.me ? booth.party.me : { id: "", deck: null };
  const myDeck = me.deck === null || me.deck === undefined ? null : me.deck;
  const host = (booth && booth.host_name) || (info && info.host) || "";

  // The friend's own file on their deck: while the TV is waiting for it, this page answers the TV and sends it.
  const myLoad = booth && myDeck !== null && booth.decks[myDeck] ? booth.decks[myDeck].load : null;
  const mineLoad = myLoad && myLoad.phone && me.id && String(myLoad.vid).startsWith(`phone:${me.id}-`) ? myLoad : null;
  const waitN = mineLoad && mineLoad.state === "wait" ? mineLoad.n : null;
  const waitVid = mineLoad ? mineLoad.vid : "";
  const myId = me.id;
  useEffect(() => {
    if (waitN === null || !myId) return undefined;
    const key = waitVid.slice(`phone:${myId}-`.length);
    const sender = startPhoneSender({ api, invite, getFile: () => fileObjs.current.get(key) || null, onInfo: (i) => setSendInfo({ ...i, vid: waitVid }) });
    let lock = null;
    let dead = false;
    const ask = async () => {
      try { if (!dead && navigator.wakeLock && document.visibilityState === "visible") lock = await navigator.wakeLock.request("screen"); } catch { /* the phone said no: fine */ }
    };
    ask();
    document.addEventListener("visibilitychange", ask);
    return () => {
      dead = true;
      sender.stop();
      document.removeEventListener("visibilitychange", ask);
      try { if (lock) lock.release(); } catch { /* ignore */ }
    };
  }, [waitN, waitVid, myId, invite]);
  const sendNote = (() => {
    if (!mineLoad) return null;
    if (mineLoad.state === "ready") return { tone: "ok", text: "Ready on the TV. Press Play when you want it." };
    if (mineLoad.state === "failed") return { tone: "warn", text: `${whyText(mineLoad.why)} Press the deck number again to try again.` };
    const i = sendInfo && sendInfo.vid === mineLoad.vid ? sendInfo : null;
    if (i && i.phase === "failed") return { tone: "warn", text: `${whyText(i.why, i.relay)} Press the deck number again to try again.` };
    if (i && i.phase === "sending") return { tone: "wait", text: `Sending to the TV: ${Math.round((i.pct || 0) * 100)}%. Keep this page open.`, pct: i.pct || 0 };
    if (i && i.phase === "finishing") return { tone: "wait", text: "The TV is putting it together. Keep this page open.", pct: 1 };
    return { tone: "wait", text: "Waiting for the TV to connect to your phone. Keep this page open." };
  })();

  return (
    <div className="max-w-3xl mx-auto" data-testid="dj-join-page">
      <div className="flex items-center gap-3">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold">DJ Party</h1>
        <Users className="w-8 h-8 text-zinc-400" />
      </div>

      {(!ready || phase === "loading") && <p className="text-zinc-500 mt-6" data-testid="dj-join-loading">Loading...</p>}

      {ready && !uid && phase === "guest" && info && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 mt-5" data-testid="dj-join-guest">
          <p className="text-lg font-semibold">{host ? `${host} invited you to a DJ party` : "You are invited to a DJ party"}</p>
          <p className="text-zinc-400 mt-1">The picture and the sound are on {host ? `${host}'s` : "the"} TV. Friends take a deck on their own phone, load songs, and play them.</p>
          <div className="mt-4" data-testid="dj-join-now">
            <div className="text-xs font-bold tracking-widest text-zinc-500 mb-1">PLAYING NOW</div>
            {info.decks.length ? (
              <ul className="space-y-1">
                {info.decks.map((d, i) => (
                  <li key={i} className="text-sm text-zinc-300 truncate">{d.title}{d.artist ? ` · ${d.artist}` : ""}{d.play ? " · playing" : ""}</li>
                ))}
              </ul>
            ) : <p className="text-sm text-zinc-500">Nothing is loaded yet.</p>}
          </div>
          {info.full ? (
            <p className="text-amber-300 mt-4" data-testid="dj-join-full">This party is full right now. Try again in a little while.</p>
          ) : (
            <>
              <p className="text-zinc-300 mt-4">To take a deck you need a free login. It takes a minute, and you come right back here.</p>
              <div className="flex flex-wrap gap-2 mt-3">
                <Link to="/register" onClick={() => rememberParty(invite)} data-testid="dj-join-register"
                  className="px-5 py-2.5 rounded-full bg-cyan-400 text-black font-bold hover:bg-cyan-300">Create a free login</Link>
                <Link to="/login" onClick={() => rememberParty(invite)} data-testid="dj-join-login"
                  className="px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/10">Log in</Link>
              </div>
              <p className="text-xs text-zinc-500 mt-3">No credit card needed.</p>
            </>
          )}
        </div>
      )}

      {phase === "over" && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 mt-5" data-testid="dj-join-over">
          <p className="text-zinc-300">That party is over, or the link is not right. Ask your friend for a new link.</p>
          <Link to="/" className="inline-block mt-3 px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/10">Go to the home page</Link>
        </div>
      )}

      {phase === "stuck" && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 mt-5" data-testid="dj-join-stuck">
          <p className="text-zinc-300">{msg}</p>
          <Link to="/" className="inline-block mt-3 px-5 py-2.5 rounded-full border border-white/20 hover:bg-white/10">Go to the home page</Link>
        </div>
      )}

      {phase === "left" && (
        <div className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 mt-5" data-testid="dj-join-left">
          <p className="text-zinc-300">You left the party.</p>
          <button type="button" onClick={rejoin} data-testid="dj-join-rejoin" className="mt-3 px-5 py-2.5 rounded-full bg-cyan-400 text-black font-bold hover:bg-cyan-300">Join again</button>
        </div>
      )}

      {phase === "in" && booth && (
        <>
          <p className="text-zinc-400 mt-2" data-testid="dj-join-intro">
            {host ? `${host}'s party. ` : ""}Take a free deck, load songs on it and play them. {host ? `${host} keeps` : "The host keeps"} the crossfader.
          </p>
          <div className="mt-4"><LookPicker look={look} tid="djj" /></div>
          <div className="dj-console" style={look.vars} data-testid="dj-join-console" data-look={look.look}>
            <div className={`text-sm flex items-center gap-2 ${booth.screen_live ? "dj-ok" : "dj-warn"}`} data-testid="dj-join-status">
              <span className="w-2.5 h-2.5 rounded-full bg-current" />
              {booth.screen_live ? "TV connected" : "Waiting for the TV..."}
            </div>

            <div className="dj-card rounded-2xl p-4 mt-3" data-testid="dj-join-seats">
              <div className="font-semibold dj-strong mb-2">Decks</div>
              <ul className="space-y-2">
                {booth.decks.map((d, i) => {
                  const seat = booth.party.seats[i];
                  const mine = myDeck === i;
                  return (
                    <li key={i} style={lit(i)} data-testid={`dj-join-seat-${i}`} className="flex items-center gap-2 rounded-xl bg-black/20 px-3 py-2">
                      <span className={`dj-led shrink-0 ${d.play ? "" : "is-off"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold dj-strong">Deck {i + 1} · {seat ? (mine ? "You" : seat.name) : "Free"}</div>
                        <div className="text-xs text-zinc-500 truncate">{d.load ? `${d.load.title}${d.play ? " · playing" : ""}` : "Empty"}</div>
                      </div>
                      {!seat && (
                        <button type="button" disabled={busy} onClick={() => take(i)} data-testid={`dj-join-take-${i}`} className="dj-sw text-sm px-3 py-1.5 rounded-full shrink-0">
                          {myDeck === null ? "Take it" : "Move here"}
                        </button>
                      )}
                      {mine && (
                        <button type="button" disabled={busy} onClick={give} data-testid="dj-join-give" className="dj-sw is-flat text-sm px-3 py-1.5 rounded-full shrink-0">Give back</button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {myDeck === null && <p className="text-xs text-zinc-500 mt-2" data-testid="dj-join-pick">Press Take it on a free deck to start.</p>}
            </div>

            {myDeck !== null && booth.decks[myDeck] && (
              <>
                <div className="mt-3 max-w-sm mx-auto" data-testid="dj-join-mine">
                  <Deck i={myDeck} deck={booth.decks[myDeck]} send={send} holder="You" />
                </div>
                {sendNote && (
                  <div className={`dj-card rounded-2xl p-3 mt-3 text-sm ${sendNote.tone === "ok" ? "dj-ok" : sendNote.tone === "warn" ? "dj-warn" : "text-zinc-300"}`} data-testid="dj-join-send">
                    <div>{sendNote.text}</div>
                    {sendNote.pct !== undefined && (
                      <div className="dj-prog h-2 rounded-full overflow-hidden mt-2"><div className="h-full" style={{ width: `${Math.round(sendNote.pct * 100)}%` }} /></div>
                    )}
                  </div>
                )}
                <Library booth={booth} send={send} decksShown={[myDeck]} fetchFiles={fetchFiles} favorites={premium}
                  mine={booth.party.files === false ? null : { fid: myId, files: myFiles, pick: () => pickRef.current && pickRef.current.click(), remove: removeMine, rights, setRights }} />
                {booth.party.files === false && (
                  <p className="text-xs text-zinc-500 mt-2" data-testid="dj-join-files-off">{host || "The host"} has turned off files from phones for this party.</p>
                )}
              </>
            )}

            <div className="mt-6 text-xs text-zinc-500 space-y-1">
              <p>{host ? `${host}'s` : "The host's"} Premium covers the party, so Premium songs play in full here. An explicit song needs your own free 18+ check.</p>
              <p>Your first name shows to the host and the other friends.</p>
              <p>A file from your phone goes straight to the TV and is not saved on Dan The Man TV. It plays on the host's screen, and whoever runs that screen could still record it, like any video on a screen. If you reload this page, choose your files again.</p>
            </div>
            <input ref={pickRef} type="file" accept="video/*,audio/*,.mp4,.m4v,.mov,.webm,.mp3,.wav,.m4a,.aac,.ogg,.flac" multiple hidden data-testid="dj-join-file-input"
              onChange={(e) => { addMine(e.target.files); e.target.value = ""; }} />
            <button type="button" onClick={leave} disabled={busy} data-testid="dj-join-leave" className="dj-sw is-flat text-sm px-4 py-2 rounded-full mt-3">Leave the party</button>
          </div>
        </>
      )}
    </div>
  );
}
